import { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { collection, query, where, onSnapshot } from 'firebase/firestore';
import { db } from '../../firebase';
import { useAuth } from '../../context/AuthContext';
import { Loader2, ShoppingBag, Search, Heart, LayoutGrid, ChevronDown, Zap, BarChart3, Trophy, Sparkles, PlusCircle } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import CourseCard from '../../components/dashboard/CourseCard';
import CourseCardSkeleton from '../../components/skeletons/CourseCardSkeleton';
import Navbar from '../../components/Navbar';
import Footer from '../../components/Footer';
import RequestCourseModal from '../../components/RequestCourseModal';

const LEVELS = { beginner: 'مبتدئ', intermediate: 'متوسط', advanced: 'متقدم' };

export default function CoursesMarketplace({ isPublicView = false, theme, toggleTheme }) {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [courses, setCourses] = useState([]);
  const [enrolledIds, setEnrolledIds] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterLevel, setFilterLevel] = useState('all');
  const [filterCategory, setFilterCategory] = useState('all');
  const [activeTab, setActiveTab] = useState('all');
  const [isLevelOpen, setIsLevelOpen] = useState(false);
  const [categoriesWithCounts, setCategoriesWithCounts] = useState([]);
  const [isRequestModalOpen, setIsRequestModalOpen] = useState(false);

  const handleRequestCourse = () => {
    if (!currentUser) {
      sessionStorage.setItem('redirectAfterLogin', location.pathname);
      navigate('/login');
      return;
    }
    setIsRequestModalOpen(true);
  };

  // Fetch published courses
  useEffect(() => {
    const q = query(collection(db, 'courses'), where('status', '==', 'published'));
    const unsub = onSnapshot(q, snap => {
      setCourses(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      setLoading(false);
    }, () => setLoading(false));
    return unsub;
  }, []);

  // Fetch enrolled course IDs if student is logged in
  useEffect(() => {
    if (!currentUser) return;
    const q = query(collection(db, 'enrollments'), where('userId', '==', currentUser.uid));
    const unsub = onSnapshot(q, snap => {
      setEnrolledIds(new Set(snap.docs.map(d => d.data().courseId)));
    });
    return unsub;
  }, [currentUser]);

  // Calculate active categories and counts
  useEffect(() => {
    const counts = {};
    courses.forEach(c => {
      if (c.category) counts[c.category] = (counts[c.category] || 0) + 1;
    });
    const filterList = Object.entries(counts)
      .map(([name, count]) => ({ name, count }))
      .sort((a, b) => b.count - a.count);
    setCategoriesWithCounts(filterList);
  }, [courses]);

  const filtered = courses.filter(c => {
    // Favorites Filter logic
    if (activeTab === 'favorites' && (!currentUser || !c.favorites?.includes(currentUser.uid))) return false;
    
    // Search & Level & Category Filter
    const matchSearch = !searchTerm || c.title?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchLevel = filterLevel === 'all' || c.level === filterLevel;
    const matchCategory = filterCategory === 'all' || c.category === filterCategory;
    return matchSearch && matchLevel && matchCategory;
  });

  const content = (
    <div className={`${isPublicView ? 'container mx-auto px-4 max-w-7xl animate-in fade-in duration-700 space-y-10 relative pt-5 pb-16' : 'animate-in fade-in duration-700 space-y-10 relative'}`} dir="rtl">
      {/* Ambient Grid Background */}
      <div className="fixed inset-0 pointer-events-none bg-[url('https://transparenttextures.com/patterns/cubes.png')] opacity-[0.02] dark:opacity-5 mix-blend-overlay"></div>

      {/* Cinematic Header */}
      <div className="relative overflow-hidden rounded-lg bg-gradient-to-br from-slate-900 via-[#0a0a0a] to-primary-950 p-10 md:p-14 border border-white/10 shadow-2xl shadow-primary-900/20 group">
        <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-primary-500/20 rounded-full blur-[100px] -translate-y-1/2 translate-x-1/3 pointer-events-none transition-transform duration-700 group-hover:scale-105"></div>
        <div className="absolute bottom-0 left-10 w-64 h-64 bg-purple-500/20 rounded-full blur-[80px] pointer-events-none"></div>

        <div className="relative z-10">
          <div className="flex items-center gap-4 mb-4">
            <div className="w-14 h-14 bg-white/10 backdrop-blur-md rounded-2xl flex items-center justify-center border border-white/10 shadow-inner">
              <ShoppingBag className="w-7 h-7 text-primary-400" />
            </div>
            <h1 className="text-4xl md:text-5xl font-black text-white drop-shadow-md tracking-tight">
              {isPublicView ? 'الدورات التدريبية' : 'سوق الدورات'}
            </h1>
          </div>
          <p className="text-zinc-400 text-lg md:text-xl font-medium max-w-2xl leading-relaxed mt-2 mb-6">
            اكتشف أحدث وأقوى الدورات الأكاديمية المصممة بعناية فائقة لترتقي بمستواك إلى القمة.
          </p>
          <button
            onClick={handleRequestCourse}
            className="bg-primary-600 hover:bg-primary-500 text-white px-6 py-3 rounded-xl font-medium flex items-center gap-2 shadow-lg shadow-primary-500/30 transition-all hover:-translate-y-1"
          >
            <PlusCircle className="w-5 h-5" />
            طلب دورة جديدة
          </button>
        </div>
      </div>

      {/* Favorites / All Toggle - Only if logged in */}
      {currentUser && (
        <div className="flex justify-center relative z-10">
          <div className="flex bg-slate-200/50 dark:bg-zinc-900/50 backdrop-blur-xl p-1.5 rounded-2xl border border-white/10 shadow-lg relative overflow-hidden">
            {[
              { id: 'all', label: 'تصفح الكل', icon: LayoutGrid, color: 'primary' },
              { id: 'favorites', label: 'المفضلة', icon: Heart, color: 'rose' }
            ].map((tab) => {
              const isActive = activeTab === tab.id;
              const Icon = tab.icon;
              
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`relative flex items-center gap-3 px-8 py-3.5 rounded-xl font-black transition-colors duration-500 z-10 group ${
                    isActive 
                      ? tab.id === 'favorites' ? 'text-rose-600 dark:text-rose-400' : 'text-primary-600 dark:text-white'
                      : 'text-slate-500 dark:text-zinc-500 hover:text-slate-800 dark:hover:text-zinc-300'
                  }`}
                >
                  {/* Sliding Background */}
                  {isActive && (
                    <motion.div
                      layoutId="activeTabBackground"
                      className={`absolute inset-0 rounded-xl shadow-md z-[-1] ${
                        tab.id === 'favorites' 
                          ? 'bg-rose-50 dark:bg-rose-500/10 border border-rose-200/50 dark:border-rose-500/20' 
                          : 'bg-white dark:bg-zinc-800 border border-white/20 dark:border-white/5'
                      }`}
                      transition={{ type: 'spring', bounce: 0.25, duration: 0.6 }}
                    />
                  )}
                  
                  <div className={`relative transition-transform duration-300 group-hover:scale-110 ${isActive ? 'scale-110' : ''}`}>
                    <Icon className={`w-5 h-5 ${isActive && tab.id === 'favorites' ? 'fill-current' : ''}`} />
                    {isActive && (
                      <motion.div
                        layoutId="activeDot"
                        className={`absolute -top-1 -right-1 w-2 h-2 rounded-full ${tab.id === 'favorites' ? 'bg-rose-500' : 'bg-primary-500'}`}
                      />
                    )}
                  </div>
                  <span className="text-sm md:text-base tracking-wide">{tab.label}</span>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Cinematic Filters */}
      <div className="flex flex-col gap-8 relative z-40 w-full">
        <div className="flex flex-col md:flex-row items-stretch md:items-center gap-4 w-full max-w-4xl mx-auto z-50">
          <div className="relative flex-1 group">
            <div className="absolute inset-y-0 right-0 pr-5 flex items-center pointer-events-none">
              <Search className="w-5 h-5 text-slate-400 dark:text-zinc-500 group-focus-within:text-primary-500 transition-colors" />
            </div>
            <input
              type="text"
              placeholder="ابحث عن دورة بأسمها أو محتواها..."
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              className="w-full pl-5 pr-14 py-4 bg-white/70 dark:bg-zinc-900/50 backdrop-blur-xl border border-slate-200/50 dark:border-white/5 rounded-2xl outline-none focus:border-primary-500 focus:ring-4 focus:ring-primary-500/10 text-slate-900 dark:text-white font-bold transition-all shadow-lg hover:shadow-xl"
            />
          </div>

          <div className="relative shrink-0 min-w-[200px]">
            <button
              onClick={() => setIsLevelOpen(!isLevelOpen)}
              className="w-full flex items-center justify-between px-6 py-4 bg-white/70 dark:bg-zinc-900/50 backdrop-blur-xl border border-slate-200/50 dark:border-white/5 rounded-2xl outline-none focus:border-primary-500 focus:ring-4 focus:ring-primary-500/10 text-slate-900 dark:text-white font-bold transition-all shadow-lg hover:shadow-xl cursor-pointer"
            >
              <span>{filterLevel === 'all' ? 'كل المستويات' : LEVELS[filterLevel]}</span>
              <ChevronDown className={`w-5 h-5 text-slate-400 transition-transform duration-300 ${isLevelOpen ? 'rotate-180 text-primary-500' : ''}`} />
            </button>

            <AnimatePresence>
              {isLevelOpen && (
                <motion.div
                  initial={{ opacity: 0, y: 10, scale: 0.95 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: 10, scale: 0.95 }}
                  className="absolute top-full right-0 left-0 mt-3 p-2 bg-white/95 dark:bg-zinc-900/90 backdrop-blur-2xl border border-slate-200 dark:border-white/10 rounded-2xl shadow-2xl z-[70] overflow-hidden"
                >
                  <button
                    onClick={() => { setFilterLevel('all'); setIsLevelOpen(false); }}
                    className={`w-full text-right px-4 py-3 rounded-xl font-bold transition-all ${filterLevel === 'all' ? 'bg-primary-500 text-white' : 'hover:bg-slate-100 dark:hover:bg-white/5 text-slate-700 dark:text-zinc-400'}`}
                  >
                    كل المستويات
                  </button>
                  {Object.entries(LEVELS).map(([v, l]) => (
                    <button
                      key={v}
                      onClick={() => { setFilterLevel(v); setIsLevelOpen(false); }}
                      className={`w-full text-right px-4 py-3 rounded-xl font-bold transition-all mt-1 ${filterLevel === v ? 'bg-primary-500 text-white' : 'hover:bg-slate-100 dark:hover:bg-white/5 text-slate-700 dark:text-zinc-400'}`}
                    >
                      {l}
                    </button>
                  ))}
                </motion.div>
              )}
            </AnimatePresence>

            {isLevelOpen && (
              <div className="fixed inset-0 z-[60]" onClick={() => setIsLevelOpen(false)} />
            )}
          </div>
        </div>

        <div className="flex flex-col items-center gap-6">
          <h3 className="text-xl font-black text-slate-900 dark:text-white">الأقسام</h3>
          <div className="flex flex-wrap justify-center gap-3 max-w-4xl">
            <button
              onClick={() => setFilterCategory('all')}
              className={`px-8 py-3 rounded-xl font-bold text-md transition-all duration-300 ${filterCategory === 'all'
                ? 'bg-primary-600 text-white border-primary-500 shadow-lg shadow-primary-500/30'
                : 'bg-slate-200 dark:bg-zinc-900 text-slate-600 dark:text-zinc-400 dark:border-white/10 hover:border-primary-300 dark:hover:border-primary-500/30'
                }`}
            >
              <span className='text-lg'>الكــــل</span> <span className='text-md'>({courses.length})</span>
            </button>
            {categoriesWithCounts.map(cat => (
              <button
                key={cat.name}
                onClick={() => setFilterCategory(cat.name)}
                className={`px-8 py-3 rounded-xl font-bold text-md transition-all duration-300 ${filterCategory === cat.name
                  ? 'bg-primary-600 text-white border-primary-500 shadow-lg shadow-primary-500/30'
                  : 'bg-slate-200 dark:bg-zinc-900 dark:text-zinc-400 border-slate-200 dark:border-white/10 hover:border-primary-300 dark:hover:border-primary-500/30'
                  }`}
              >
                <span className='text-lg'>{cat.name}</span> <span className='text-md'>({cat.count})</span>
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Courses Grid */}
      {loading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 md:gap-8 relative z-10 w-full pt-8">
          {Array.from({ length: 8 }).map((_, i) => (
            <CourseCardSkeleton key={i} />
          ))}
        </div>
      ) : filtered.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-24 text-slate-500 bg-white/50 dark:bg-zinc-900/40 backdrop-blur-2xl rounded-lg border border-slate-200/50 dark:border-white/5 shadow-inner grow relative z-10">
          <div className={`w-24 h-24 rounded-full flex items-center justify-center mb-6 transition-all ${activeTab === 'favorites' ? 'bg-rose-50 dark:bg-rose-500/10' : 'bg-slate-100 dark:bg-black/30'}`}>
            {activeTab === 'favorites' ? <Heart className="w-10 h-10 text-rose-300 dark:text-rose-500/50" /> : <Search className="w-10 h-10 text-slate-300 dark:text-zinc-600" />}
          </div>
          <p className="text-2xl font-black mb-2 text-slate-700 dark:text-white">
             {activeTab === 'favorites' ? 'لا يوجد دورات في المفضلة' : 'لا توجد دورات مطابقة'}
          </p>
          <p className="text-slate-500 dark:text-zinc-400 font-medium text-center">
            {activeTab === 'favorites' ? 'قم بإضافة الدورات التي تعجبك إلى المفضلة للعودة إليها لاحقاً.' : 'حاول بكلمات بحث أخرى أو قم بتغيير الفلتر.'}
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 md:gap-8 relative z-10 w-full pt-8">
          {filtered.map(course => (
            <CourseCard
              key={course.id}
              course={course}
              isEnrolled={enrolledIds.has(course.id)}
              isPublicView={isPublicView}
            />
          ))}
        </div>
      )}

      <RequestCourseModal isOpen={isRequestModalOpen} onClose={() => setIsRequestModalOpen(false)} />
    </div>
  );

  if (isPublicView) {
    return (
      <>
        <Navbar theme={theme} toggleTheme={toggleTheme} />
        <div className="min-h-screen flex flex-col pb-16" dir="rtl">
          {content}
        </div>
        <Footer />
      </>
    );
  }

  return content;
}
